<template>
	<Headroom :offset="20" @pin="onHeaderPin" @unpin="onHeaderUnpin">
		<header
			class="main-header relative z-50 flex h-20 flex-wrap"
			:class="{
				'megamenu-active': isMegaMenuActive,
				'mt-12 md:mt-8': hasHeaderMargin && !showSecurityNotice,
			}"
			:style="securityNoticeMarginStyle"
			@mouseleave="hideAllMegaMenu(menu)"
		>
			<div
				class="main-header--nav relative z-20 flex w-full justify-between px-5 duration-200"
				:class="[
					isMegaMenuActive
						? 'bg-gray-50'
						: excludedRoutes[route.path] ||
							  isAfterExcludedRoutes(route.path)
							? 'lg:bg-gray-50'
							: 'bg-white xl:bg-gray-50',
				]"
			>
				<div class="flex w-60 items-center">
					<CarbarLink
						href="/"
						:class="[showMobileMenu ? 'lg:flex' : '']"
						@click="hideMobileMenu"
						@mouseover="hideAllMegaMenu(menu)"
					>
						<IconCarbarLogo />
					</CarbarLink>
				</div>
				<div v-if="showMenu" class="flex-end ml-auto flex">
					<button
						class="hamburger hamburger--spring js-hamburger relative ml-auto flex items-center lg:hidden"
						:class="[showMobileMenu ? 'is-active' : '']"
						type="button"
						@click="toggleMobileMenu"
					>
						<span class="hamburger-box">
							<span class="hamburger-inner"></span>
						</span>
					</button>
				</div>

				<div
					v-if="showMenu"
					class="hidden flex-1 justify-center lg:flex"
				>
					<ul class="m-0 flex list-none gap-x-2">
						<li
							v-for="menuItem in menu"
							:key="menuItem.label"
							class="flex cursor-pointer"
						>
							<template v-if="menuItem.dropdown">
								<ClientOnly>
									<DropdownUserMenu
										:position="'right'"
										:show-caret="false"
										:menu-items="menuItem.dropdown"
										class="flex items-center"
									>
										<template #menu-button>
											{{ menuItem.label }}
										</template>
									</DropdownUserMenu>
								</ClientOnly>
							</template>
							<CarbarLink
								v-else
								class="flex items-center px-4 font-medium delay-75 duration-200"
								:font-size="'text-base'"
								:class="[
									'text-gray-600 hover:text-blue-400',
									menuItem.megaMenuState === 'hidden' ||
									menuItem.megaMenuState === 'hiding'
										? ''
										: 'text-blue-400',
								]"
								:to="getMenuLink(menuItem.link)"
								:underline="false"
								@touchstart.prevent.stop="
									onMenuItemMouseOver(menuItem)
								"
								@mouseover="onMenuItemMouseOver(menuItem)"
								@mouseleave="onMenuItemMouseLeave"
							>
								{{ menuItem.label }}
							</CarbarLink>
						</li>
					</ul>
				</div>

				<div
					v-if="showMenu"
					class="hidden w-60 flex-1 items-center justify-end lg:flex lg:flex-none"
				>
					<div
						v-if="!isListingOnly"
						class="flex h-full items-center space-x-4 lg:space-x-3"
					>
						<div v-if="auUser.getIsSigningOut" class="h-4 w-4">
							<IconsIconLoading />
						</div>

						<DropdownUserMenu
							v-if="auUser.isLoggedIn"
							:menu-items="userMenu"
							class="group cursor-pointer"
						>
							<template #menu-button>
								<UserAccountIcon
									:is-novated="auUser.isNovated"
								/>
							</template>
							<template #caret>
								<div class="on-open-rotate-90 ml-1">
									<IconArrowDown
										class="text-gray-500 duration-200 group-hover:rotate-90 group-hover:text-blue-400"
									/>
								</div>
							</template>
						</DropdownUserMenu>

						<CarbarLink
							v-if="!auUser.isLoggedIn"
							:href="'/secure/login'"
							:underline="false"
							class="group flex h-fit items-center"
						>
							<IconButton
								v-if="!auUser.isLoggedIn"
								class="hidden lg:flex"
								@click="onLoginClick"
								@mouseover="hideAllMegaMenu(menu)"
							>
								<template #icon>
									<IconUser
										class="h-3 w-3 transition-all group-hover:text-orange-400"
									/>
								</template>
								Sign in
							</IconButton>
							<span class="flex lg:hidden">Sign in</span>
						</CarbarLink>
						<CarbarLink
							v-if="!auUser.isLoggedIn"
							:href="`/sign-up`"
							:underline="false"
							class="flex h-fit items-center"
							@click="signupClickHandler()"
						>
							<CarbarButton
								:background-color="'bg-blue-400 hover:bg-sky-600'"
								@mouseover="hideAllMegaMenu(menu)"
							>
								Join for free
							</CarbarButton>
						</CarbarLink>
					</div>
				</div>
			</div>

			<div class="absolute bottom-0 hidden w-full lg:block">
				<PersonalMegaMenu
					:menu="menu"
					:car-count="carbar.vehicleCount.subscription"
				/>
				<BusinessMegaMenu :menu="menu" />
				<SupportMegaMenu :menu="menu" />
			</div>

			<div
				class="mobile-menu absolute inset-0 h-screen max-h-screen w-full overflow-y-auto bg-white pb-10 pt-20 duration-200 lg:hidden"
				:class="[
					showMobileMenu
						? 'translate-x-0 opacity-100'
						: '-translate-x-full opacity-0',
				]"
			>
				<HeaderMobile :menu="menu" />
			</div>
		</header>
	</Headroom>

	<CarbarStickyHeaders />

	<CarbarCampaigns v-if="showCampaign" />
</template>

<script setup>
import IconArrowDown from '@/assets/svg/common/ico-arrow_down.svg'
import IconUser from '@/assets/svg/common/ico-user.svg'
import IconCarbarLogo from '@/assets/svg/logo-carbar-v3.svg'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import IconButton from '@/components/buttons/IconButton.vue'
import CarbarCampaigns from '@/components/campaigns/CarbarCampaigns.vue'
import DropdownUserMenu from '@/components/dropdowns/DropdownUserMenu.vue'
import HeaderMobile from '@/components/headers/HeaderMobile.vue'
import BusinessMegaMenu from '@/components/headers/megamenus/BusinessMegaMenu.vue'
import PersonalMegaMenu from '@/components/headers/megamenus/PersonalMegaMenu.vue'
import SupportMegaMenu from '@/components/headers/megamenus/SupportMegaMenu.vue'
import UserAccountIcon from '@/components/headers/UserAccountIcon.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import CarbarStickyHeaders from '@/components/sticky-header/CarbarStickyHeaders.vue'
import { useCarbarStore } from '@/stores/carbar'
import { useCarbarAuAuthStore } from '@/stores/carbar-au-auth'
import { headerModule } from '@/utils/Header.js'
import Headroom from '@/vendor/headroom/headroom.vue'

const route = useRoute()

const emit = defineEmits(['login', 'logout', 'join'])

const carbar = useCarbarStore()
const auUser = useCarbarAuAuthStore()
const isListingOnly = useIsListingOnly()

const props = defineProps({
	showMenu: {
		type: Boolean,
		default: true,
		required: false,
	},
	showCampaign: {
		type: Boolean,
		default: true,
		required: false,
	},
})

const excludedRoutes = {
	'/': true,
	'/cars/subscribe': true,
	'/cars/buy/used': true,
	'/terms-and-conditions': true,
	'/privacy-policy': true,
}

const isAfterExcludedRoutes = (path) => {
	const routePaths = Object.keys(excludedRoutes)

	for (const routePath of routePaths) {
		if (path.startsWith(`${routePath}/`)) {
			return true
		}
	}
	return false
}

const { showAnnouncement } = useAnnouncementSchedule()
const { isNovatedLeaseStickyActive } = showNovatedLeaseCampaign()
const { isSecondaryCampaignActive } = showStickyCampaign()
const { showSecurityNotice, securityNoticeMarginStyle } = useSecurityNotice()

const hasHeaderMargin = computed(() => {
	const isSecondaryActive = !!isSecondaryCampaignActive.value
	const isNovatedActive = !!isNovatedLeaseStickyActive.value
	const isAnnouncementShowing = !!showAnnouncement.value

	return (
		isNovatedActive ||
		(isSecondaryActive && props.showCampaign) ||
		isAnnouncementShowing ||
		showSecurityNotice.value
	)
})

const { showMegaMenu, hideAllMegaMenu, toggleMobileMenu, hideMobileMenu } =
	headerModule()

const showMobileMenu = computed(() => carbar.header.showMobileMenu)

const isHomepage = computed(() => route.path === '/')

const groupSubsBasePath = '/partners/carsales/group-subscription'
const isGroupSubscriptionEnabled = useIsGroupSubscriptionEnabled()

const menu = ref([
	{
		key: 'personal-cars',
		label: 'Personal',
		link: '/cars/subscribe',
		current: false,
		showMegaMenu: false,
		hideMegaMenuInterval: null,
		hideMegaMenuTimeout: 500,
		megaMenuState: 'hidden',
		megaMenu: [
			[
				{
					label: 'How it works',
					link: '/car-subscription',
					current: false,
				},
				{
					label: 'Electric car subscription',
					link: '/electric-vehicles',
					current: false,
				},
				{
					label: 'Compare car subscriptions vs leasing',
					link: '/compare-car-subscriptions-vs-leasing',
					current: false,
				},
				{
					label: 'Trade in to subscribe',
					link: '/sell-my-car',
					current: false,
				},
				...(isGroupSubscriptionEnabled
					? [
							{
								label: 'Group subscription',
								link: isHomepage.value
									? `${groupSubsBasePath}?ref=homepage`
									: groupSubsBasePath,
								current: false,
							},
						]
					: []),
			],
		],
	},
	{
		key: 'business-cars',
		label: 'Business',
		link: '/business',
		current: false,
		showMegaMenu: false,
		hideMegaMenuInterval: null,
		hideMegaMenuTimeout: 500,
		megaMenuState: 'hidden',
		megaMenu: [
			[
				{
					label: 'Novated lease',
					link: '/business/novated-lease',
					current: false,
				},
				{
					label: 'Novated subscription',
					link: '/business/novated-subscription',
					current: false,
				},
				{
					label: 'Novated EV subscription',
					link: '/business/novated-ev-subscription',
					current: false,
				},
				{
					label: 'Fleet subscription',
					link: '/business/fleet-subscription',
					current: false,
				},
				{
					label: 'Employee benefits',
					link: '/business/tools-of-trade/employee-benefits',
					current: false,
				},
			],
		],
	},
	{
		key: 'support',
		label: 'Support',
		link: 'void',
		current: false,
		showMegaMenu: false,
		hideMegaMenuInterval: null,
		hideMegaMenuTimeout: 500,
		megaMenuState: 'hidden',
		megaMenu: [
			[
				{
					label: 'FAQs',
					link: 'https://support.carbar.com.au',
					current: false,
					target: '_blank',
				},
			],
		],
	},
])

const isMegaMenuActive = computed(
	() =>
		menu.value.filter(
			(item) =>
				item.megaMenuState === 'hiding' ||
				item.megaMenuState === 'shown'
		).length
	// return menu.value.filter((item) => item.showMegaMenu).length
)

let menuTimer = null

const areAllMenusHidden = (menuHidden) =>
	menuHidden.every((item) => item.megaMenuState === 'hidden')

const onMenuItemMouseOver = (menuItem) => {
	clearTimeout(menuTimer)

	if (!areAllMenusHidden(menu.value)) {
		menuTimer = setTimeout(() => {
			hideAllMegaMenu(menu.value)
			showMegaMenu(menuItem)
		}, 200)
	} else {
		hideAllMegaMenu(menu.value)
		menuTimer = setTimeout(() => {
			showMegaMenu(menuItem)
		}, 400)
	}
}

const onMenuItemMouseLeave = () => {
	clearTimeout(menuTimer)
}

const userMenu = ref([
	{
		label: 'Dashboard',
		href: `/dashboard`,
	},
	{
		label: 'Account',
		href: `/dashboard/account`,
	},
	{
		label: 'Help & support',
		href: `https://support.carbar.com.au`,
	},
	{
		label: 'Logout',
		onclick: () => {
			signOut()
		},
	},
])

const onHeaderPin = () => {
	// document.body.classList.remove(...['header-unpinned'])
	document.body.classList.add(...['header-pinned'])
}

const onHeaderUnpin = () => {
	document.body.classList.remove(...['header-pinned'])
}

const onLoginClick = () => {
	emit('login')
}

const getMenuLink = (hrefLink) => (hrefLink === 'void' ? '' : hrefLink)

const signupClickHandler = () => {
	gtmTrack('join_for_free_click')
}

const gtmTrack = (event) => {
	trackGtmEvent({ event })
}
</script>
